/* eslint-disable camelcase */
import './LocationSearch.scss';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete, LoadScript } from '@react-google-maps/api';
import classNames from 'classnames';

import toast from 'atoms/toast';
import {
  clearLocationData,
  editLocationForm,
} from 'store/reducers/locationForm';

const placesLibrary = ['places'];

function LocationSearch({
  label = 'Address',
  address,
  editLocation,
  placeholder = 'Type to search',
  className,
}) {
  const dispatch = useDispatch();
  const [searchResult, setSearchResult] = useState(address);
  const autocompleteComponent = document.getElementById('autocomplete');
  const autocompleteInput = document.getElementById('location-search');
  const locationForm = useSelector((state) => state.locationForm);
  const { address_1, latitude, longitude } = locationForm;
  const [edited, setEdited] = useState(false);
  const invalidAddress = address_1 === 'invalid_address';
  const hasNoAddress = edited && !latitude && !longitude;
  const hasError =
    (invalidAddress || hasNoAddress) &&
    document.activeElement !== autocompleteInput;

  useEffect(() => {
    if (autocompleteComponent) {
      autocompleteComponent.addEventListener('keydown', disableFormSubmittion);
    }
    return () => {
      if (autocompleteComponent) {
        autocompleteComponent.removeEventListener(
          'keydown',
          disableFormSubmittion
        );
      }
    };
  }, [autocompleteComponent]);

  useEffect(() => {
    if (invalidAddress) {
      toast({ text: 'Please input a valid address with a number' });
    }
  }, [locationForm.address_1]);

  const disableFormSubmittion = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  function onLoad(autocomplete) {
    setSearchResult(autocomplete);
  }

  async function onPlaceChanged() {
    if (searchResult != null) {
      const place = await searchResult.getPlace();
      popullateAddress(place);
      const latitude = place.geometry.location.lat();
      const longitude = place.geometry.location.lng();
      dispatch(editLocation({ key: 'latitude', value: latitude }));
      dispatch(editLocation({ key: 'longitude', value: longitude }));
    }
  }

  const popullateAddress = (location) => {
    const { address_components: components } = location;

    if (!components) return;

    const fullAddress = [];
    components.forEach((el) => {
      if (el.types.includes('route')) {
        fullAddress.push(el.long_name);
      }
      if (el.types.includes('street_number')) {
        fullAddress.push(el.long_name);
      }
      if (el.types.includes('postal_code')) {
        dispatch(editLocation({ key: 'postalCode', value: el.long_name }));
      }
      if (el.types.includes('locality')) {
        dispatch(editLocation({ key: 'locality', value: el.long_name }));
      }
      if (el.types.includes('neighborhood')) {
        dispatch(editLocation({ key: 'neighborhood', value: el.long_name }));
      }
      if (el.types.includes('administrative_area_level_1')) {
        dispatch(editLocation({ key: 'region', value: el.short_name }));
      }
      const hasStreetNumber =
        components.filter((el) => el.types.includes('street_number')).length >
        0;
      setTimeout(() => {
        dispatch(
          editLocation({
            key: 'address_1',
            value: hasStreetNumber ? fullAddress.join(' ') : 'invalid_address',
          })
        );
      }, 500);
    });
  };

  const clearErrors = () => {
    if (invalidAddress) {
      dispatch(editLocationForm({ key: 'address_1', value: null }));
    }
  };

  const clearValues = (e) => {
    if (!edited) setEdited(true);
    dispatch(clearLocationData());
    clearErrors();
  };

  return (
    <div className="location-search">
      {label && <p className="label">{label}</p>}
      <p className={classNames(hasError && !invalidAddress && 'c-red', 'f13')}>
        Please select from the dropdown
      </p>
      <div id="searchColumn" className="mt-15">
        <LoadScript
          googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
          libraries={placesLibrary}
        >
          <Autocomplete
            restrictions={{ country: 'us' }}
            onPlaceChanged={(e) => onPlaceChanged(e)}
            onLoad={onLoad}
          >
            <input
              onFocus={(e) => e.target.setAttribute('autoComplete', 'none')}
              onChange={clearValues}
              id="location-search"
              className={classNames(
                'input-field w-100',
                hasError ? 'input-error' : '',
                className
              )}
              placeholder={placeholder}
              autoComplete="false"
              type="search"
            />
          </Autocomplete>
        </LoadScript>
      </div>
    </div>
  );
}

export default LocationSearch;
